/* UI-SP2-6 — Due Diligence Assessments listing page. Routed at
   /dd/assessments. Mirrors the analyst team's expected design: full
   history of all DD reports with filter strip, status/classification
   pills, risk-flag counts, and per-row actions. */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { listAssessments, listProjects, getProject } from '../services/api';
import type { AssessmentListItem } from '../types/api';
import { formatListingDate as formatDate } from '../utils/formatListingDate';

const PAGE_SIZE = 25;

/* Sprint 5 client feedback 2026-06-23 (FE Four Issue 1): Status /
   Classification / Flags chips re-aligned to the canonical .badge
   palette so Completed → green (was blue), Failed → red, Processing
   → orange, etc. A leading status-dot dot makes each chip scannable
   at a glance, matching the design doc's "Small status dot
   indicator" requirement. */
/* Sprint 5 client feedback 2026-06-25 (TSG OIA FE 250626 AN doc #3) —
   Reports page Status + Classification chips were inconsistent in
   size/casing/padding/color. Both pills now route through the
   canonical `.badge` palette (badge-pass / badge-watch / badge-fail
   / badge-info / badge-neutral) for uniform pill style. Raw status
   tokens like `in_progress` are explicitly mapped to Title Case
   ("In Progress") with the appropriate colour band; unknown tokens
   still title-case via _titleCase so no chip leaks underscores or
   raw enum-case to the user. */
function _titleCase(s: string): string {
  if (!s) return s;
  return s
    .replace(/_/g, ' ')
    .split(' ')
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1).toLowerCase() : w))
    .join(' ');
}

function StatusPill({ status }: { status: string }) {
  const variant: Record<string, { cls: string; label: string }> = {
    completed:   { cls: 'badge-pass',    label: 'Completed' },
    complete:    { cls: 'badge-pass',    label: 'Completed' },
    in_progress: { cls: 'badge-info',    label: 'In Progress' },
    analysing:   { cls: 'badge-watch',   label: 'Processing' },
    ingesting:   { cls: 'badge-watch',   label: 'Processing' },
    flagged:     { cls: 'badge-watch',   label: 'Flagged' },
    failed:      { cls: 'badge-fail',    label: 'Failed' },
    reject:      { cls: 'badge-fail',    label: 'Reject' },
    cancelled:   { cls: 'badge-neutral', label: 'Cancelled' },
    pending:     { cls: 'badge-neutral', label: 'Pending' },
  };
  const key = (status || '').toLowerCase();
  const v = variant[key] ?? { cls: 'badge-neutral', label: _titleCase(status) };
  const dot =
    v.cls === 'badge-pass'  ? 'online'  :
    v.cls === 'badge-watch' ? 'warn'    :
    v.cls === 'badge-fail'  ? 'offline' :
    v.cls === 'badge-info'  ? 'info'    :
    '';
  return (
    <span className={`badge ${v.cls}`}>
      {dot && <span className={`status-dot ${dot}`} aria-hidden="true" />}
      {v.label}
    </span>
  );
}

function _classify(overall: string): string {
  const norm = (overall || '').toLowerCase();
  if (norm === 'pass' || norm === 'completed' || norm === 'complete' || norm === 'regular') return 'badge-pass';
  if (norm === 'fail' || norm === 'reject') return 'badge-fail';
  if (norm === 'watch' || norm === 'conditional' || norm === 'opportunistic') return 'badge-watch';
  if (norm === 'conservative') return 'badge-info';
  return 'badge-neutral';
}

function ClassificationPill({ overall, category }: { overall: string | null; category: string | null }) {
  if (!overall && !category) return <span className="badge badge-neutral">Pending</span>;
  // Combined chip — colour driven by `overall` (the harshest of the
  // two when classified). Labels are title-cased so "in_progress"
  // never leaks to the UI as a raw enum token.
  const cls = overall ? _classify(overall) : (category ? _classify(category) : 'badge-neutral');
  const parts = [overall, category]
    .filter((s): s is string => Boolean(s && s.trim()))
    .map(_titleCase);
  const label = parts.join(' · ');
  return <span className={`badge ${cls}`}>{label}</span>;
}

/* Sprint 5 client feedback 2026-06-23 (FE Four Issue 1): Flags
   count chip will land alongside the AssessmentListItem schema
   when `flag_count` / `critical_count` are added to the API. The
   chip class set (.badge.badge-neutral / .badge.badge-watch /
   .badge.badge-fail) is already wired so the only change needed
   when the data arrives is a one-line cell at the table render.
   Component intentionally not exported yet — schema-bound. */

/* UI-SP2-9 (re-opened #2) — compact pagination range. Matches the
   numbered-with-ellipsis pattern used in the reference design. */
function buildPageRange(current: number, total: number): Array<number | 'ellipsis'> {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i);
  const out: Array<number | 'ellipsis'> = [];
  const last = total - 1;
  const lo = Math.max(1, current - 1);
  const hi = Math.min(last - 1, current + 1);
  out.push(0);
  if (lo > 1) out.push('ellipsis');
  for (let p = lo; p <= hi; p++) out.push(p);
  if (hi < last - 1) out.push('ellipsis');
  out.push(last);
  return out;
}

export function DDAssessmentsListPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState<AssessmentListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filter state — client-side filtering on top of the DD+SPA+RISK fetch.
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [classificationFilter, setClassificationFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  // Sprint 5 client feedback 2026-06-22 — Reports listing restyle:
  // analyst filter (design audit doc item #3). Server doesn't index
  // by analyst yet, so this is client-side over the loaded page.
  const [analystFilter, setAnalystFilter] = useState('');

  // Pending filter values — what the inputs hold before Apply is clicked.
  const [pendingSearch, setPendingSearch] = useState('');
  const [pendingType, setPendingType] = useState('');
  const [pendingStatus, setPendingStatus] = useState('');
  const [pendingClassification, setPendingClassification] = useState('');
  const [pendingDate, setPendingDate] = useState('');
  const [pendingAnalyst, setPendingAnalyst] = useState('');

  /* Sprint 5 client feedback 2026-07-08 (UI Ticket — Reports bulk
     actions removal) — bulk-select state, header tri-state checkbox,
     toolbar buttons (Bulk Download / Bulk Export / Archive), and
     per-row checkboxes all removed. None of the bulk actions were
     wired to working handlers; leaving the selection UI without any
     actions would be worse than removing it. Row click still opens
     the report (single-row action preserved). */

  useEffect(() => {
    /* Sprint 5 — this page is now the unified Reports listing.
       Fetch both DD and SPA module rows in parallel and merge them
       so analysts see every report in one table. The page-level
       Type filter (filterType in state) still narrows client-side
       to one module if the analyst wants. */
    setIsLoading(true);
    Promise.all([
      listAssessments({ module_type: 'DD', limit: PAGE_SIZE, offset: page * PAGE_SIZE }),
      listAssessments({ module_type: 'SPA', limit: PAGE_SIZE, offset: page * PAGE_SIZE }),
      /* Sprint 5 — synthesise RISK rows. The Risk Assessment lives
         in its own table (not `assessments`), so listAssessments
         can't return it. Pull the project list + enrich each
         project; any project with a complete (or degraded) risk
         row gets a synthetic AssessmentListItem with
         module_type='RISK'. The synthetic row's assessment_id is
         the RISK-YYYY-NNNN derived from the project audit_id so
         the row's action buttons can route to the risk-report
         page. */
      listProjects({ limit: 50 }).then(async (lp) => {
        const enriched = await Promise.all(
          lp.projects.map(async (p) => {
            try {
              const d = await getProject(p.audit_id);
              if (!d.risk_assessment) return null;
              const rs = d.risk_assessment.status;
              /* Sprint 5 client feedback 2026-06-23 — also surface
                 running / pending / failed Risk rows in the reports
                 list. Previously only complete/degraded showed up,
                 so an analyst who kicked off a Risk run had no way
                 to find their way back to the live processing
                 page from the reports listing. Map every Risk
                 status to an AssessmentListItem status the table
                 understands. */
              if (!rs || rs === 'locked') return null;
              const realRiskId =
                d.risk_assessment.id ||
                p.audit_id.replace('AUDIT-', 'RISK-');
              const statusMap: Record<string, string> = {
                complete: 'completed',
                degraded: 'completed',
                pending: 'in_progress',
                in_progress: 'in_progress',
                running: 'in_progress',
                failed: 'failed',
                flagged: 'completed',
              };
              const overallStatusMap: Record<string, string> = {
                complete: 'completed',
                degraded: 'flagged',
                pending: 'in_progress',
                in_progress: 'in_progress',
                running: 'in_progress',
                failed: 'failed',
                flagged: 'flagged',
              };
              return {
                assessment_id: realRiskId,
                module_type: 'RISK',
                // Sprint 5 client feedback 2026-07-03 — Risk rows carry
                // the source project name on both project_name (the
                // authoritative field per the revised schema) and
                // deal_name (backwards-compat mirror), matching what
                // the BE DD/SPA listing endpoint now emits.
                project_name: p.project_name,
                deal_name: p.project_name,
                analyst_name: null,
                status: statusMap[rs] ?? rs,
                document_count: 0,
                created_at:
                  d.risk_assessment.completed_at ??
                  d.risk_assessment.triggered_at ??
                  p.created_at,
                overall_status: overallStatusMap[rs] ?? rs,
                overall_category: null,
                _project_audit_id: p.audit_id,
                _risk_status_raw: rs,
              } as AssessmentListItem & { _project_audit_id: string; _risk_status_raw: string };
            } catch {
              return null;
            }
          }),
        );
        return enriched.filter(Boolean) as Array<
          AssessmentListItem & { _project_audit_id: string; _risk_status_raw?: string }
        >;
      }),
    ])
      .then(([dd, spa, riskRows]) => {
        const merged = [...dd.items, ...spa.items, ...riskRows].sort((a, b) => {
          const ad = a.created_at ? new Date(a.created_at).getTime() : 0;
          const bd = b.created_at ? new Date(b.created_at).getTime() : 0;
          return bd - ad;
        });
        setItems(merged);
        setTotal(dd.total + spa.total + riskRows.length);
      })
      .catch(() => setError('Failed to load reports'))
      .finally(() => setIsLoading(false));
  }, [page]);

  const filteredItems = useMemo(() => {
    return items.filter((it) => {
      if (search) {
        const q = search.toLowerCase();
        // Sprint 5 client feedback 2026-07-03 — search hits both
        // project_name and the legacy deal_name so a user typing an
        // old label still finds the row until backfill completes.
        const haystack = [
          it.project_name ?? '',
          it.deal_name ?? '',
          it.analyst_name ?? '',
          it.assessment_id,
        ].join(' ').toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      if (statusFilter && it.status !== statusFilter) return false;
      if (classificationFilter && it.overall_status !== classificationFilter) return false;
      if (typeFilter && it.module_type !== typeFilter) return false;
      if (analystFilter && it.analyst_name !== analystFilter) return false;
      if (dateFilter && it.created_at) {
        const ageDays = (Date.now() - new Date(it.created_at).getTime()) / 86400000;
        if (dateFilter === '7d' && ageDays > 7) return false;
        if (dateFilter === '30d' && ageDays > 30) return false;
        if (dateFilter === '90d' && ageDays > 90) return false;
      }
      return true;
    });
  }, [items, search, statusFilter, classificationFilter, typeFilter, analystFilter, dateFilter]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const firstShown = filteredItems.length === 0 ? 0 : page * PAGE_SIZE + 1;
  const lastShown = page * PAGE_SIZE + filteredItems.length;

  function applyFilters() {
    setSearch(pendingSearch);
    setStatusFilter(pendingStatus);
    setClassificationFilter(pendingClassification);
    setDateFilter(pendingDate);
    setTypeFilter(pendingType);
    setAnalystFilter(pendingAnalyst);
    setPage(0);
  }

  function clearFilters() {
    setPendingSearch(''); setPendingStatus(''); setPendingClassification('');
    setPendingDate(''); setPendingType(''); setPendingAnalyst('');
    setSearch(''); setStatusFilter(''); setClassificationFilter('');
    setDateFilter(''); setTypeFilter(''); setAnalystFilter('');
    setPage(0);
  }

  return (
    <div>
      {/* Sprint 5 client feedback 2026-06-22 — Reports listing restyle:
          removed the .list-header-band peach gradient wrapper per
          design audit doc item #1 (matches the Dashboard pass 1
          treatment in 61dd8c0). Title row + KPI cards + filter strip
          now sit on the plain page background; visual separation
          comes from card stack borders, not a coloured banner. */}
      <div>
        <div className="page-topbar page-topbar-compact">
          <div>
            <p className="t-xs t-muted" style={{ margin: 0, marginBottom: 'var(--s2)' }}>
              <a href="/dashboard" onClick={(e) => { e.preventDefault(); navigate('/dashboard'); }} style={{ color: 'var(--muted)', textDecoration: 'none' }}>
                Dashboard
              </a>
              {' › '}
              <span style={{ color: 'var(--text-sec)' }}>All Reports</span>
            </p>
            <h1 className="page-topbar-title">All Reports</h1>
            <p className="page-topbar-sub">
              DD assessments, SPA legal reviews, and Risk reports across every project
            </p>
          </div>
          <div className="topbar-actions">
            <button className="btn btn-accent" type="button" onClick={() => navigate('/projects/new')}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              New Project
            </button>
          </div>
        </div>

        {/* Sprint 5 client feedback 2026-06-23 (FE Four Issue 1):
            KPI summary cards moved out of inline grid styles into
            the shared .kpi-strip / .kpi-card system in index.css,
            and the whole strip is wrapped in .reports-content so it
            picks up the same horizontal padding as the table below.
            That fixes the "cards span wider than the table" gripe
            from the design doc — cards, filters, and table now all
            share the same left/right edge. */}
        <div className="reports-content">
        <section className="kpi-strip" aria-label="Reports summary">
          {(() => {
            const ddCount = items.filter((i) => i.module_type === 'DD').length;
            const spaCount = items.filter((i) => i.module_type === 'SPA').length;
            const totalCount = items.length;
            const ddPct = totalCount ? Math.round((ddCount / totalCount) * 100) : 0;
            const spaPct = totalCount ? Math.round((spaCount / totalCount) * 100) : 0;
            const pending = items.filter(
              (i) =>
                i.status !== 'completed' &&
                i.status !== 'complete' &&
                i.status !== 'failed',
            ).length;
            const tiles: { label: string; value: number; sub: string; variant: string }[] = [
              { label: 'Total Reports',  value: totalCount, sub: 'across DD, SPA & Risk', variant: '' },
              { label: 'DD Reports',     value: ddCount,    sub: `${ddPct}% of total`,    variant: 'dd' },
              { label: 'SPA Legal Review', value: spaCount, sub: `${spaPct}% of total`,   variant: 'spa' },
              { label: 'Pending Actions',value: pending,    sub: pending ? 'awaiting analyst review' : 'all complete', variant: pending ? 'watch' : 'pass' },
            ];
            return tiles.map((c) => (
              <article key={c.label} className={`kpi-card${c.variant ? ' kpi-card--' + c.variant : ''}`}>
                <div className="kpi-card-label">{c.label}</div>
                <div className="kpi-card-value">{c.value}</div>
                <div className="kpi-card-sub">{c.sub}</div>
              </article>
            ));
          })()}
        </section>
        </div>

        {/* Filter strip — inside the peach header band */}
        <div className="list-header-filter-row">
        <div className="filter-strip">
          <div className="filter-search">
            <svg className="filter-search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input
              type="search"
              className="filter-search-input"
              placeholder="Search by deal name, analyst, or job ID..."
              value={pendingSearch}
              onChange={(e) => setPendingSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') applyFilters(); }}
            />
          </div>
          <select className="filter-select" value={pendingType} onChange={(e) => setPendingType(e.target.value)} aria-label="Type filter">
            <option value="">All Types</option>
            <option value="DD">DD</option>
            <option value="SPA">SPA</option>
            <option value="RISK">Risk</option>
          </select>
          <select className="filter-select" value={pendingStatus} onChange={(e) => setPendingStatus(e.target.value)} aria-label="Status filter">
            <option value="">All Statuses</option>
            <option value="completed">Completed</option>
            <option value="analysing">Processing</option>
            <option value="ingesting">Ingesting</option>
            <option value="failed">Failed</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <select className="filter-select" value={pendingClassification} onChange={(e) => setPendingClassification(e.target.value)} aria-label="Classification filter">
            <option value="">All Classifications</option>
            <option value="Pass">Pass / Approve</option>
            <option value="Watch">Watch / Conditional</option>
            <option value="Fail">Fail / Reject</option>
            <option value="flagged">Flagged (Risk)</option>
          </select>
          <select className="filter-select" value={pendingDate} onChange={(e) => setPendingDate(e.target.value)} aria-label="Date filter">
            <option value="">All Dates</option>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
          </select>
          {/* Sprint 5 client feedback 2026-06-22 — Reports listing
              restyle: Analyst dropdown (design audit doc item #3).
              Options auto-derived from analyst_name values on the
              loaded page. Disabled while items are loading. */}
          <select
            className="filter-select"
            value={pendingAnalyst}
            onChange={(e) => setPendingAnalyst(e.target.value)}
            aria-label="Analyst filter"
            disabled={items.length === 0}
          >
            <option value="">All Analysts</option>
            {Array.from(
              new Set(
                items
                  .map((i) => i.analyst_name)
                  .filter((n): n is string => Boolean(n)),
              ),
            )
              .sort()
              .map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
          </select>
          <button type="button" className="btn btn-secondary btn-sm" onClick={clearFilters}>Clear</button>
          <button type="button" className="btn btn-accent btn-sm" onClick={applyFilters}>Apply</button>
          {/* Sprint 5 client feedback 2026-07-08 (UI Ticket — Reports
              Export removal) — the list-level Export button was a
              stub that alerted "Export not yet wired up." Removed
              per the ticket; per-row / detail-page export paths (if
              any) are untouched. */}
        </div>

        {/* Sprint 5 client feedback 2026-06-22 — Reports listing
            restyle: removed the inline "Showing N of M assessments"
            counter that sat above the table. The pagination footer
            already carries the same counter ("Showing X-Y of N
            reports"); having both was noise. Loading state surfaces
            via the spinner inside the table card. */}
        </div>
      </div>

      <div className="page-body">
        {error && (
          <div className="alert alert-fail" style={{ marginBottom: 'var(--s4)' }}>{error}</div>
        )}

        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          {isLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--s10)' }}>
              <span className="spinner spinner-lg" />
            </div>
          ) : filteredItems.length === 0 ? (
            <div style={{ padding: 'var(--s10)', textAlign: 'center' }}>
              <p className="t-muted t-sm">No DD assessments match your filters.</p>
            </div>
          ) : (
            /* Sprint 5 client feedback 2026-07-08 (UI Ticket — Reports
               bulk actions removal) — bulk toolbar + tri-state header
               checkbox removed; the corresponding per-row checkbox
               column is dropped below. Only single-row actions remain. */
            <div className="table-wrap">
              <table className="data-table data-table--spacious" aria-label="Due Diligence assessments">
                <thead>
                  <tr>
                    {['Type', 'Project Name', 'Docs', 'Analyst', 'Submitted', 'Status', 'Classification', 'Actions'].map((h) => (
                      <th key={h}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredItems.map((item) => {
                    const projectAuditId = (item as AssessmentListItem & { _project_audit_id?: string })._project_audit_id;
                    const isRisk = item.module_type === 'RISK';
                    // Sprint 5 client feedback 2026-06-22 — Reports
                    // listing restyle: canonical .badge classes for
                    // Type pill (design audit doc item #12). Matches
                    // the rest of the app and is dark-mode-aware via
                    // the palette overrides in ead2640 / 6ae5442.
                    const typeBadgeClass =
                      item.module_type === 'SPA' ? 'badge-spa'
                      : isRisk ? 'badge-watch'
                      : 'badge-dd';
                    const typeLabel = isRisk ? 'Risk' : item.module_type;
                    /* Sprint 5 client feedback 2026-06-23 — for an
                       in-flight Risk row (pending / running /
                       in_progress), route to the live processing
                       page instead of the (empty) risk-report. The
                       _risk_status_raw carries the actual server
                       status untransformed; statusMap above
                       collapsed it for table display. */
                    const riskRaw = (item as unknown as Record<string, unknown>)._risk_status_raw;
                    const riskInProgress = isRisk && (
                      riskRaw === 'pending' || riskRaw === 'running' || riskRaw === 'in_progress'
                    );
                    const rowHref = isRisk && projectAuditId
                      ? (riskInProgress
                          ? `/projects/${projectAuditId}/risk/processing`
                          : `/projects/${projectAuditId}/risk-report`)
                      : `/assessments/${item.assessment_id}`;
                    return (
                    <tr
                      key={item.assessment_id}
                      style={{ cursor: 'pointer' }}
                      onClick={() => navigate(rowHref)}
                    >
                      <td>
                        <span className={`badge ${typeBadgeClass}`}>
                          {typeLabel}
                        </span>
                      </td>
                      <td style={{ maxWidth: 240 }}>
                        {/* Sprint 5 client feedback 2026-07-03 — Project
                            Name column now reads project_name first
                            (Project.project_name via the BE JOIN);
                            deal_name is the legacy fallback for older
                            standalone rows. "Ad-hoc" is emitted by the
                            BE when neither source is present. */}
                        <div className="td-name" style={{ fontWeight: 600 }}>
                          {item.project_name ?? item.deal_name ?? <span className="t-muted">—</span>}
                        </div>
                        <div
                          style={{
                            fontFamily: 'var(--font-mono)',
                            fontSize: '0.68rem',
                            color: 'var(--muted)',
                            letterSpacing: '0.02em',
                            marginTop: 2,
                          }}
                        >
                          {item.assessment_id}
                        </div>
                      </td>
                      <td className="t-xs" style={{ textAlign: 'center' }}>{item.document_count}</td>
                      <td>{item.analyst_name ?? <span className="t-muted">—</span>}</td>
                      <td style={{ whiteSpace: 'nowrap' }} className="t-xs">{formatDate(item.created_at)}</td>
                      <td><StatusPill status={item.status} /></td>
                      <td><ClassificationPill overall={item.overall_status} category={item.overall_category} /></td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <div className="row-actions">
                          <button
                            type="button"
                            className="row-action-btn"
                            data-tip="View report"
                            aria-label="View report"
                            onClick={() => navigate(rowHref)}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                          </button>
                          {/* DOCX download is only meaningful for DD/SPA
                              assessment reports, not for the synthesised
                              RISK rows whose report is a dedicated page. */}
                          {!isRisk && (
                            <button
                              type="button"
                              className="row-action-btn"
                              data-tip="Download DOCX"
                              aria-label="Download DOCX"
                              onClick={() => navigate(`/assessments/${item.assessment_id}?download=docx`)}
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {!isLoading && total > 0 && (
            <div className="table-pager">
              <span className="t-xs t-muted">
                Showing {firstShown}-{lastShown} of {total} reports
              </span>
              <div className="pager-controls">
                <button
                  type="button"
                  className="pager-btn pager-icon"
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                  aria-label="Previous page"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="15 18 9 12 15 6"/></svg>
                </button>
                {buildPageRange(page, totalPages).map((token, i) =>
                  token === 'ellipsis' ? (
                    <span key={`e-${i}`} className="pager-ellipsis" aria-hidden="true">…</span>
                  ) : (
                    <button
                      key={token}
                      type="button"
                      className={`pager-btn pager-num${token === page ? ' active' : ''}`}
                      onClick={() => setPage(token)}
                      aria-current={token === page ? 'page' : undefined}
                      aria-label={`Page ${token + 1}`}
                    >
                      {token + 1}
                    </button>
                  ),
                )}
                <button
                  type="button"
                  className="pager-btn pager-icon"
                  onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                  aria-label="Next page"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="9 18 15 12 9 6"/></svg>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
